import React from "react";
import { Node, useReactFlow } from "reactflow";
import { useSelector } from "react-redux";
import { faCheck, faCode, faLongArrowLeft } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IoPlayOutline } from "react-icons/io5";
import CopyToClipboard from "react-copy-to-clipboard";
import { faClipboard } from "@fortawesome/free-regular-svg-icons";
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { coldarkDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import axios from "axios";
import { Select } from "../../form/select";
import { webURL } from "../../../services/API";
import { operationType } from "../../../services/types";
import { handleWorkflowSave } from "../../../services/method";
import { RootState } from "../../../redux/store";
import { Main } from "./style";
import { createPortal } from "react-dom";
import { PopupOverlay } from "../popupOverlay";

interface stateType { isToken: boolean, isCopy: boolean, result?: string, isRunDisabled?: boolean }

export function APIProject({ data, handleValuesFeatures }: { data: projectType | null, handleValuesFeatures: (values: any) => void }) { 
   const userToken = useSelector((state: RootState) => state.user.data?.token);
   const [state, setState] = React.useState<stateType>({ isToken: false, isCopy: false });
   const [languages, setLanguages] = React.useState([
      { id: 1, name: 'Python', code: '', isActive: true },
      { id: 2, name: 'JavaScript', code: '', isActive: false }, 
      { id: 3, name: 'cURL', refId: 'bash', code: '', isActive: false },
   ]);
   const { getNodes, getEdges } = useReactFlow();

   // ---
   const handleSelectLanguages = (selectId: number) => {
      setLanguages((languages) => languages.map((language) => {
         if (language.id === selectId) return { ...language, isActive: true }; else return { ...language, isActive: false };
      }));
   }

   // ---
   const createJsonData = (): string => {
      const nodes: Node<operationType>[] = getNodes();
      let data: any = {};
      
      for (const node of nodes) {
         if (node.data.action !== 'output' && node.data.value) data[node.data.index as string] = node.data.value;
      }
      for (const key in data) {
         if (data[key].result) {
            data[key] = { ...data[key] };
            delete data[key].result;
            delete data[key].isTemporaryMemory;
         }
      }
      return JSON.stringify(data, null, 1).replace(/\n/g, '');
   }

   // ---
   const handleExampleRun = async () => {
      setState(state => ({ ...state, isRunDisabled: true }));

      const nodes = getNodes();
      const edges = getEdges();
      await handleWorkflowSave(data?._id as string, userToken as string, nodes, edges);

      const response = await axios.post(
         `${webURL}/run/${data?._id}`, JSON.parse(createJsonData()), 
         { headers: { 'Authorization': `Bearer ${data?.apiKey}`}, validateStatus: () => true }
      );
      setState(state => ({ ...state, result: JSON.stringify(response.data, null, 1), isRunDisabled: false }));
   }

   // ---
   const languageAtive = React.useMemo(() => languages.find(language => language.isActive), [languages]);

   return createPortal(
      <PopupOverlay onClick={ _=> handleValuesFeatures({ isAPI: false }) }>
         <Main onClick={ e => e.stopPropagation() }>
            <div className="close" onClick={ _=> handleValuesFeatures({ isExport: true, isAPI: false }) }>
               <FontAwesomeIcon icon={ faLongArrowLeft } />
            </div>
            <div className="title">
               <span className="icon">
                  <FontAwesomeIcon icon={ faCode } />
               </span>
               <div>
                  <h3>Export as API</h3>
                  <p>Use your application via API</p>
               </div>
            </div>
            <div className="options">
               <Select data={ languages } handleSelectItem={ handleSelectLanguages } />
               <div>
                  <button onClick={ _=> setState(state => ({ ...state, isToken: !state.isToken })) }>
                     { state.isToken ? 'Hide Token' : 'Show Token' }
                  </button>
                  <button onClick={ handleExampleRun } disabled={ state.isRunDisabled }>
                     <IoPlayOutline />
                     <span>Run</span>
                  </button>
               </div>
            </div>
            <div className={`code ${state.result ? 'withResult' : ''}`}>
               <div className="title">
                  <span>{ languageAtive?.refId || languageAtive?.name.toLowerCase() } example</span>
                  <CopyToClipboard 
                     text={ buildCodeByLanguage(languageAtive?.name, { isToggle: true, id: data?._id, value: data?.apiKey }, createJsonData()) }
                     onCopy={ _=> {
                        setState(state => ({ ...state, isCopy: true }));
                        setTimeout(() => setState(state => ({ ...state, isCopy: false })), 500);
                     }}
                  >
                     <div className="copy" style={{ color: state.isCopy ? '#53c68c' : '#eee' }}>
                        <FontAwesomeIcon icon={ state.isCopy ? faCheck : faClipboard } />
                        <span>{ state.isCopy ? 'Copied' : 'Copy'}</span>
                     </div>
                  </CopyToClipboard>
               </div>
               <SyntaxHighlighter
                  language={ languageAtive?.refId || languageAtive?.name.toLowerCase() }
                  style={ coldarkDark } 
                  showLineNumbers
               >
                  { buildCodeByLanguage(languageAtive?.name, { isToggle: !!state.isToken, id: data?._id, value: data?.apiKey }, createJsonData()) }
               </SyntaxHighlighter>
            </div>
            { state.result ? <div className="result">
               <div className="points"><span></span><span></span><span></span></div>
               <pre>{ state.result }</pre>
            </div> : null }
         </Main>
      </PopupOverlay>,
      document.body
   );
}

// ---
const buildCodeByLanguage = (language: any, apiKey: { isToggle: boolean, id: any, value: any }, jsonData: string): string => {   
   if (language === 'Python') {
      return `import requests

API_URL = '${webURL}/run/${apiKey.id}'
headers = {
   'Authorization': 'Bearer ${apiKey.isToggle ? apiKey.value : 'XXXXXXXXXXXXX'}',
   'Content-Type': 'application/json'
}

def executeQuery(payload):
   response = requests.post(API_URL, headers=headers, json=payload)
   return response.json()

output = executeQuery(${jsonData})`;

   } else if (language === 'JavaScript') {
      return `async function executeQuery(data) {
   const response = await fetch(
      '${webURL}/run/${apiKey.id}',
      {
         headers: {
            'Authorization': 'Bearer ${apiKey.isToggle ? apiKey.value : 'XXXXXXXXXXXXX'}',
            'Content-Type': 'application/json'
         },
         method: 'POST',
         body: JSON.stringify(data),
      }
   );
   const result = await response.json();
   return result;
}

executeQuery(${jsonData}).then((response) => {
   console.log(response);
});`;
   } else if (language === 'cURL') {
      return `curl "${webURL}/run/${apiKey.id}"
 -X POST 
   -d '${jsonData}'
   -H "Content-Type: application/json" 
   -H "Authorization: Bearer ${apiKey.isToggle ? apiKey.value : 'XXXXXXXXXXXXX'}"`;
   }
   return '';
}